import {
  TypeValue,
  FurnitureValue,
  BillsValue,
  SuitableForValue,
  RoomsValue,
  Filters,
} from 'ha/types/SearchFilters';
import { CanonicalCollections } from 'ha/constants/SearchPageCollections';

import { diffDay } from 'ha/helpers/hadate';
import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';

const isFiltersEmpty = ({
  types,
  furniture,
  bills,
  registration,
  rules,
  recentlyAdded,
  rooms,
  gender,
  suitableFor,
}: Filters): boolean => {
  if (types && types.length !== 0) {
    return false;
  }

  if (furniture) {
    return false;
  }

  if (bills) {
    return false;
  }

  if (registration) {
    return false;
  }

  if (recentlyAdded) {
    return false;
  }

  if (suitableFor && suitableFor.length !== 0) {
    return false;
  }

  if (rules && rules.length !== 0) {
    return false;
  }

  if (gender && gender.length !== 0) {
    return false;
  }

  if (rooms && rooms.bedroomCount.length !== 0) {
    return false;
  }

  return true;
};

const isPrivateRoomForRent = (filters: Filters): boolean => {
  const { types, ...otherFilters } = filters;

  return (
    !!types &&
    types.length === 1 &&
    types?.includes(TypeValue.PRIVATE_ROOM) &&
    isFiltersEmpty(otherFilters)
  );
};

const isStudioForRent = (filters: Filters): boolean => {
  const { types, ...otherFilters } = filters;

  return (
    !!types &&
    types.length === 1 &&
    types?.includes(TypeValue.STUDIO) &&
    isFiltersEmpty(otherFilters)
  );
};

const isSharedRoomForRent = (filters: Filters): boolean => {
  const { types, ...otherFilters } = filters;

  return (
    !!types &&
    types.length === 1 &&
    types?.includes(TypeValue.SHARED_ROOM) &&
    isFiltersEmpty(otherFilters)
  );
};

const isApartmentForRent = (filters: Filters): boolean => {
  const { types, rooms, ...otherFilters } = filters;

  const isApartmentForRent =
    !!types && types.length === 1 && types?.includes(TypeValue.APARTMENT);

  const withoutRooms = !rooms || rooms?.bedroomCount.length === 0;

  return isApartmentForRent && withoutRooms && isFiltersEmpty(otherFilters);
};

const isFurnishedApartments = (filters: Filters): boolean => {
  const { types, rooms, furniture, ...otherFilters } = filters;

  const isApartmentForRent = !!types && types?.includes(TypeValue.APARTMENT);
  const withoutRooms = !rooms || rooms?.bedroomCount.length === 0;

  return (
    isApartmentForRent &&
    withoutRooms &&
    !!furniture &&
    furniture?.includes(FurnitureValue.FURNISHED) &&
    isFiltersEmpty(otherFilters)
  );
};

const isOneBedroomApartments = (filters: Filters): boolean => {
  const { types, rooms, ...otherFilters } = filters;

  const isApartmentForRent =
    !!types && types.length === 1 && types?.includes(TypeValue.APARTMENT);

  return (
    isApartmentForRent &&
    !!rooms &&
    rooms?.bedroomCount.includes(RoomsValue.ONE) &&
    rooms?.bedroomCount.length === 1 &&
    isFiltersEmpty(otherFilters)
  );
};

const isTwoBedroomApartments = (filters: Filters): boolean => {
  const { types, rooms, ...otherFilters } = filters;

  const isApartmentForRent =
    !!types && types.length === 1 && types?.includes(TypeValue.APARTMENT);

  return (
    isApartmentForRent &&
    !!rooms &&
    rooms?.bedroomCount.includes(RoomsValue.TWO) &&
    rooms?.bedroomCount.length === 1 &&
    isFiltersEmpty(otherFilters)
  );
};

const isCheapAccommodation = (filters: Filters): boolean => {
  const { price, ...otherFilters } = filters;

  return (
    !!price &&
    price?.min === 0 &&
    price.max === 0 &&
    price.currency === DEFAULT_CURRENCY &&
    isFiltersEmpty(otherFilters)
  );
};

const isLongTermRentals = (filters: Filters): boolean => {
  const { dates, ...otherFilters } = filters;

  return (
    !!dates &&
    !dates.endDate &&
    !!dates.startDate &&
    diffDay(dates?.startDate, new Date().toISOString()) === 0 &&
    isFiltersEmpty(otherFilters)
  );
};

const isStudentAccomodation = (filters: Filters): boolean => {
  const { furniture, bills, suitableFor, ...otherFilters } = filters;

  if (!furniture || !bills || !suitableFor) {
    return false;
  }

  return (
    furniture?.includes(FurnitureValue.FURNISHED) &&
    bills?.includes(BillsValue.INCLUDED) &&
    suitableFor?.includes(SuitableForValue.STUDENTS) &&
    isFiltersEmpty(otherFilters)
  );
};

const isStudentRoom = (filters: Filters): boolean => {
  const { types, ...otherFilters } = filters;

  return (
    !!types &&
    types?.includes(TypeValue.PRIVATE_ROOM) &&
    types?.includes(TypeValue.SHARED_ROOM) &&
    isStudentAccomodation(otherFilters)
  );
};

export default function (filters: Filters): string | undefined {
  if (isPrivateRoomForRent(filters)) {
    return CanonicalCollections.PrivateRoomForRent;
  }

  if (isStudioForRent(filters)) {
    return CanonicalCollections.StudioForRent;
  }

  if (isSharedRoomForRent(filters)) {
    return CanonicalCollections.SharedRoomForRent;
  }

  if (isApartmentForRent(filters)) {
    return CanonicalCollections.ApartmentForRent;
  }

  if (isFurnishedApartments(filters)) {
    return CanonicalCollections.FurnishedApartments;
  }

  if (isOneBedroomApartments(filters)) {
    return CanonicalCollections.OneBedroomApartments;
  }

  if (isTwoBedroomApartments(filters)) {
    return CanonicalCollections.TwoBedroomApartments;
  }

  if (isCheapAccommodation(filters)) {
    return CanonicalCollections.CheapAccommodation;
  }

  if (isLongTermRentals(filters)) {
    return CanonicalCollections.LongTermRentals;
  }
  if (isStudentAccomodation(filters)) {
    return CanonicalCollections.StudentAccommodation;
  }

  if (isStudentRoom(filters)) {
    return CanonicalCollections.StudentRoom;
  }

  return undefined;
}
